import {
  QuestionnaireTemplate,
  useExportQuestionnaireSummaryReportMutation,
} from '@report/graphql';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  FormControlLabel,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { dayjs, triggerFileDownload } from '@shared/utils';
import { useEffect, useState } from 'react';

import { useQuestionnaireReportSearchParams } from '../../hooks';

interface TemplateOption {
  value: QuestionnaireTemplate;
  label: string;
}

const TEMPLATE_OPTIONS: TemplateOption[] = [
  { value: QuestionnaireTemplate.Standard, label: 'Template 1 — Standard' },
  { value: QuestionnaireTemplate.LongForm, label: 'Template 2 — Long form' },
  { value: QuestionnaireTemplate.ShortForm, label: 'Template 3 — Short form' },
];

interface QuestionnaireReportExportModalProps {
  open: boolean;
  onClose: () => void;
}

export default function QuestionnaireReportExportModal(
  props: Readonly<QuestionnaireReportExportModalProps>,
) {
  const { open, onClose } = props;

  const { showSnackbar } = useSnackbar();

  const [{ examStartDate, examEndDate, examCenter, examCourse }] =
    useQuestionnaireReportSearchParams();

  const [selectedTemplates, setSelectedTemplates] = useState<QuestionnaireTemplate[]>([]);

  useEffect(() => {
    if (open) setSelectedTemplates([]);
  }, [open]);

  const handleTemplateChange = (value: QuestionnaireTemplate) => {
    setSelectedTemplates((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value],
    );
  };

  const formattedDate = `${examStartDate ? dayjs(examStartDate).format('D MMM BBBB') : ''} - ${examEndDate ? dayjs(examEndDate).format('D MMM BBBB') : ''}`;
  const formattedExamCenter = examCenter.includes('all') ? 'Select All' : examCenter.join(', ');
  const formattedExamCourse = examCourse.includes('all') ? 'Select All' : examCourse.join(', ');
  const formattedTotal =
    selectedTemplates.length === 0
      ? 'กรุณาเลือกอย่างน้อย 1 Template'
      : `${selectedTemplates.length} Templates`;

  const [exportQuestionnaireSummaryReport, { loading: isExporting }] =
    useExportQuestionnaireSummaryReportMutation({
      onCompleted: (data) => {
        const signedUrl = data.reportExportQuestionnaireSummaryReport.signedUrl;
        if (!signedUrl) {
          showSnackbar({ message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง', variant: 'error' });
          onClose();
          return;
        }

        triggerFileDownload(signedUrl);
        showSnackbar({ message: 'ส่งออกข้อมูลเรียบร้อยแล้ว', variant: 'success' });
        onClose();
      },
      onError: () => {
        showSnackbar({ message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง', variant: 'error' });
        onClose();
      },
    });

  const handleExport = () => {
    exportQuestionnaireSummaryReport({
      variables: {
        input: {
          startedAt: (examStartDate ? dayjs(examStartDate).startOf('day').toDate() : null) as Date,
          endedAt: (examEndDate ? dayjs(examEndDate).endOf('day').toDate() : null) as Date,
          examCenterCodes: examCenter.includes('all') ? null : examCenter,
          examCourseCodes: examCourse.includes('all') ? null : examCourse,
          templates: selectedTemplates,
        },
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs'>
      <DialogTitle onClose={onClose} loading={isExporting}>
        ยืนยันการส่งออกข้อมูล
      </DialogTitle>
      <DialogContent dividers>
        <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(3) })}>
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(1) })}>
            <Text variant='body1Semibold'>เลือก Template ที่ต้องการ Export</Text>
            <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(1) })}>
              {TEMPLATE_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  label={option.label}
                  control={
                    <Checkbox
                      checked={selectedTemplates.includes(option.value)}
                      onChange={() => handleTemplateChange(option.value)}
                    />
                  }
                />
              ))}
            </Flex>
          </Flex>
          <Divider />
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(1) })}>
            <Text variant='body1Semibold'>สรุปข้อมูลที่ต้องการ</Text>
            <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
              <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
                Date:
              </Text>
              <Text>{formattedDate}</Text>
            </Flex>
            <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
              <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
                Exam Center:
              </Text>
              <Text>{formattedExamCenter}</Text>
            </Flex>
            <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
              <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
                Exam Course:
              </Text>
              <Text>{formattedExamCourse}</Text>
            </Flex>
            <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
              <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
                รวมทั้งหมด:
              </Text>
              <Text>{formattedTotal}</Text>
            </Flex>
          </Flex>
        </Flex>
      </DialogContent>
      <DialogActions>
        <Flex sx={(theme) => ({ gap: theme.spacing(2), justifyContent: 'flex-end' })}>
          <Button size='large' variant='outlined' color='secondary' onClick={onClose}>
            ยกเลิก
          </Button>
          <Button
            size='large'
            loading={isExporting}
            onClick={handleExport}
            disabled={selectedTemplates.length === 0 || isExporting}
          >
            ส่งออกข้อมูล
          </Button>
        </Flex>
      </DialogActions>
    </Dialog>
  );
}
